/**
 * Centralized Typography System
 *
 * Single source of truth for all typography styles across the platform.
 * Provides consistent font sizes, weights, line heights, and colors.
 *
 * Usage:
 * import { typography } from '@/lib/theme/typography';
 * <p style={typography.body.medium}>{text}</p>
 */

import { colors } from './colors';

/**
 * Typography Tokens
 *
 * All typography values use inline style objects to avoid Tailwind config conflicts.
 * This ensures consistent rendering across the platform regardless of global CSS.
 */
export const typography = {
  /**
   * Headings
   */
  heading: {
    h1: {
      fontSize: '32px',
      fontWeight: 700,
      lineHeight: '40px',
      color: colors.text.primary,
    },
    h2: {
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: '32px',
      color: colors.text.primary,
    },
    h3: {
      fontSize: '20px',
      fontWeight: 600,
      lineHeight: '28px',
      color: colors.text.primary,
    },
    h4: {
      fontSize: '18px',
      fontWeight: 600,
      lineHeight: '28px',
      color: colors.text.primary,
    },
    h5: {
      fontSize: '16px',
      fontWeight: 600,
      lineHeight: '24px',
      color: colors.text.primary,
    },
    h6: {
      fontSize: '14px',
      fontWeight: 600,
      lineHeight: '20px',
      color: colors.text.primary,
    },
  },

  /**
   * Body Text
   */
  body: {
    large: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
      color: colors.text.primary,
    },
    medium: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
      color: colors.text.primary,
    },
    small: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '18px',
      color: colors.text.tertiary,
    },
    xsmall: {
      fontSize: '11px',
      fontWeight: 400,
      lineHeight: '16px',
      color: colors.text.tertiary,
    },
  },

  /**
   * Label Text (for form labels, section headers)
   */
  label: {
    large: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      color: colors.text.secondary,
    },
    medium: {
      fontSize: '13px',
      fontWeight: 500,
      lineHeight: '18px',
      color: colors.text.secondary,
    },
    small: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '16px',
      color: colors.text.secondary,
    },
  },

  /**
   * UI Text (buttons, pills, badges)
   */
  ui: {
    button: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
    },
    pill: {
      fontSize: '13px',
      fontWeight: 500,
      lineHeight: '18px',
    },
    badge: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '16px',
    },
    caption: {
      fontSize: '11px',
      fontWeight: 400,
      lineHeight: '16px',
      color: colors.text.quaternary,
    },
  },

  /**
   * Data Display (tables, metrics, stats)
   */
  data: {
    // Table headers
    tableHeader: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '18px',
      color: colors.text.tertiary,
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
    },
    // Table cells
    tableCell: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '20px',
      color: colors.text.primary,
    },
    // Metric values (large numbers)
    metricValue: {
      fontSize: '24px',
      fontWeight: 600,
      lineHeight: '32px',
      color: colors.text.primary,
    },
    // Metric labels
    metricLabel: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      color: colors.text.tertiary,
    },
    // Metric descriptions
    metricDescription: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '18px',
      color: colors.text.quaternary,
    },
  },

  /**
   * Specialized Text Styles
   */
  special: {
    // Code/monospace
    code: {
      fontSize: '13px',
      fontWeight: 400,
      lineHeight: '20px',
      fontFamily: 'monospace',
      color: colors.text.primary,
    },
    // Links
    link: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '20px',
      color: colors.interactive.primary,
      textDecoration: 'none' as const,
    },
    // Helper/hint text
    helper: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: '18px',
      color: colors.text.quaternary,
    },
    // Error messages
    error: {
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '18px',
      color: colors.status.error.base,
    },
  },
} as const;

/**
 * Helper function to merge typography styles with custom colors
 *
 * @example
 * const style = withColor(typography.body.medium, colors.status.success.base);
 */
function withColor(
  baseStyle: Record<string, any>,
  color: string
): Record<string, any> {
  return {
    ...baseStyle,
    color,
  };
}

/**
 * Helper function to merge typography styles with custom properties
 *
 * @example
 * const style = withStyles(typography.body.medium, { fontWeight: 600 });
 */
function withStyles(
  baseStyle: Record<string, any>,
  customStyles: Record<string, any>
): Record<string, any> {
  return {
    ...baseStyle,
    ...customStyles,
  };
}
